import * as React from 'react'
import { Category } from '@ugu/types'
import getCategories from '@services/api/category/getCategories'
import { queryClient } from 'pages/_app'

type State = {
  categories: Category[]
  setCategories: React.Dispatch<React.SetStateAction<Category[]>>
}
type CountProviderProps = {
  children: React.ReactNode
  initial?: Category[]
}

interface Categories extends State {
  isLoading: boolean
}

const CountStateContext = React.createContext<Categories | undefined>(undefined)
const CategoriesProvider = ({
  children,
  initial = undefined,
}: CountProviderProps) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(!initial)
  const [categories, setCategories] = React.useState<Category[]>(initial || [])

  const fetchCategories = async () => {
    setIsLoading(true)
    try {
      const categories = await queryClient.fetchQuery({
        queryKey: ['categories'],
        queryFn: getCategories,
      })
      setCategories(categories)
    } catch (error) {
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    if (!initial && !categories.length) {
      fetchCategories()
    }
  }, [initial])

  return (
    <CountStateContext.Provider
      value={{ categories, isLoading, setCategories }}
    >
      {children}
    </CountStateContext.Provider>
  )
}

const useCategoriesState = () => {
  const context = React.useContext(CountStateContext)

  if (context === undefined) {
    throw new Error(
      'useCategoriesState must be used within a CategoriesProvider'
    )
  }
  return context
}

export { CategoriesProvider, useCategoriesState }
